import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';
import { awaitJob } from '../../lib/await-job.js';

// ============================================================================
// Otto-Lister — Varianten-Übersicht (im Variantenvater).
//
// Read-only Übersicht je Variantenkind: SKU (Deep-Link nach Vario), Achsen, Otto-Kanal-Status,
// Pflichtfeld-Fortschritt (%), Aktion. Die eigentliche EAN/Preis/Bild-Pflege passiert NICHT hier,
// sondern im reduzierten Kind-Lister — und erst NACHDEM der Händler den Otto-Verkaufskanal für
// die Variante angelegt hat ("Verkaufskanal anlegen" → otto-variant-channel-create). Nur Kinder
// mit angelegtem Kanal werden an Otto übertragen. Keine Browser-Dialoge.
// ============================================================================

export interface VariantDataCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  registerSaver?: (id: string, fn: () => Promise<void>) => void;
}
export interface VariantDataArgs { listingId: string }

interface Row {
  articleId: string;
  sku: string | null;
  attrs: Record<string, string>;
  ean: string | null;
  priceEur: number | null;
  /** Mapping-Vorschau (grossSalesPrice) — nur als Platzhalter-Hinweis, wenn priceEur (echter
   *  Kanalpreis) noch fehlt. Wird NIE als Feldwert übernommen. */
  pricePreviewEur?: number | null;
  eanSource?: 'article' | 'listing' | 'mapping' | null;
  priceSource?: 'channel' | 'mapping' | null;
  onChannel: boolean;
  channelListingId: string | null;
  childOttoListingId: string | null;
  eanOk: boolean;
  priceOk: boolean;
  hasImage: boolean;
  completeness: number;
  /** Bestand/Min/Max/Lager aus otto.listing_stock_resolved (otto-variant-rows), fail-soft null. */
  varioStock?: number | null;
  effMin?: number | null;
  effMax?: number | null;
  ownMin?: number | null;
  ownMax?: number | null;
  ownStorageIds?: string[] | null;
  effStorageIds?: string[] | null;
}

interface WarehouseRow { id: string; number?: string | null; displayName?: string | null; salesStorage?: boolean }

export async function renderVariantDataGrid(host: HTMLElement, ctx: VariantDataCtx, args: VariantDataArgs): Promise<void> {
  // Beim ERSTEN Öffnen den Vario-Bestand frisch laden (READ-ONLY, kein Push/Abgleich) — analog
  // Angebotsverwaltung, damit die „verfügbarer Bestand"-Spalte aktuell ist statt auf dem Cache-
  // Stand des letzten Cron-Laufs. Das host-Flag verhindert einen erneuten Refresh bei internen
  // Reloads (Save / Kanal-Anlage / nach dem Refresh selbst) → keine Schleife. Non-blocking, best-effort.
  if (host.dataset['stockAutoRefreshed'] !== '1') {
    host.dataset['stockAutoRefreshed'] = '1';
    void (async () => {
      try {
        const { data, error } = await ctx.supabase.rpc('otto_enqueue_vario_stock_refresh', { _storefront: 'otto' });
        if (error) throw new Error(error.message);
        const jobId = typeof data === 'string' ? data : null;
        if (jobId) await awaitJob(ctx.supabase, jobId, { timeoutMs: 3 * 60_000, pollIntervalMs: 2500 });
        await renderVariantDataGrid(host, ctx, args); // Reload mit frischen Beständen
      } catch (e) {
        // Beim Öffnen bewusst leise — der Cron holt den Bestand ohnehin nach.
        console.warn('[otto-variant-data] auto vario-stock refresh failed', e instanceof Error ? e.message : e);
      }
    })();
  }
  host.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Variantenübersicht…</div>`;

  let rows: Row[]; let channelResolved = false; let varioBaseUrl = '';
  let warehouses: WarehouseRow[] = [];
  try {
    const [res, whJson] = await Promise.all([
      callFn<{ rows: Row[]; channelId: string | null; varioBaseUrl?: string; meta: { channelResolved?: boolean; note?: string } }>(
        ctx, 'otto-variant-rows', { action: 'list', listingId: args.listingId },
      ),
      fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-warehouses-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify({ marketplace: 'otto' }),
      }).then((r) => r.json().catch(() => ({}))).catch(() => ({})),
    ]);
    warehouses = (whJson as { warehouses?: WarehouseRow[] })?.warehouses ?? [];
    rows = res.rows ?? [];
    // Stabil nach Artikelnummer (SKU) sortieren — sonst springt die Reihenfolge beim Reload
    // (Vario liefert Kombinationen unsortiert). Natürliche Sortierung (numerische Teile).
    rows.sort((a, b) => (a.sku ?? '').localeCompare(b.sku ?? '', undefined, { numeric: true, sensitivity: 'base' }));
    channelResolved = res.meta?.channelResolved ?? (res.channelId != null);
    varioBaseUrl = res.varioBaseUrl ?? '';
    if (rows.length === 0) {
      host.innerHTML = banner('info', res.meta?.note ?? 'Keine Variantenkinder gefunden.');
      return;
    }
  } catch (e) {
    host.innerHTML = banner('error', `Variantenübersicht nicht ladbar: ${(e as Error).message}`);
    return;
  }

  const axisKeys = Array.from(new Set(rows.flatMap((r) => Object.keys(r.attrs))));
  const onChannelCount = rows.filter((r) => r.childOttoListingId != null).length;
  host.innerHTML = `
    <div class="otto-vd">
      <div class="otto-vd-head">
        <div class="kl-sec-lab" style="margin:0;">Varianten · OTTO-Kanäle &amp; Schnell-Pflege</div>
      </div>
      <p class="otto-vd-sub">
        Lege je Variante den Otto-Verkaufskanal an — danach kannst du EAN &amp; Preis hier schnell pflegen
        (oder im Kind-Lister mit Feld-Mapping). <b>Nur Varianten mit angelegtem Kanal werden an OTTO übertragen.</b>
        ${channelResolved ? '' : '<br><b>Achtung:</b> Otto-Verkaufskanal nicht aufgelöst — Anlage/Preis-Speichern schlägt fehl (Einstellungen prüfen).'}
      </p>
      ${onChannelCount > 0 ? `<div class="otto-vd-count">${onChannelCount}/${rows.length} Varianten im Kanal.</div>` : ''}
      <div class="otto-vtable-wrap">
        <table class="kl-vtable">
          <thead><tr>
            <th>SKU</th>
            ${axisKeys.map((a) => `<th>${escapeHtml(a)}</th>`).join('')}
            <th>Otto-Kanal</th>
            <th>Pflichtfelder</th>
            <th>EAN</th>
            <th>Otto-Preis (€)</th>
            <th>Bestand</th>
            <th>Min</th>
            <th>Max</th>
            <th>Lager</th>
          </tr></thead>
          <tbody>
            ${rows.map((r) => rowHtml(r, axisKeys, varioBaseUrl, warehouses)).join('')}
          </tbody>
        </table>
      </div>
    </div>`;

  // EAN-Inline-Validierung (nur Kanal-Zeilen haben Inputs).
  host.querySelectorAll<HTMLInputElement>('input[data-vd-ean]').forEach((inp) => {
    const paint = () => {
      const v = inp.value.trim();
      const hint = inp.parentElement?.querySelector<HTMLElement>('[data-vd-ean-hint]');
      if (!hint) return;
      if (!v) { hint.textContent = ''; hint.className = 'otto-vd-hint'; inp.classList.remove('is-invalid', 'is-warn'); return; }
      const r = validateEan(v);
      if (!r.ok) { hint.textContent = r.reason ?? 'ungültig'; hint.className = 'otto-vd-hint otto-vd-hint--err'; inp.classList.add('is-invalid'); inp.classList.remove('is-warn'); }
      else if (r.leadingTwo) { hint.textContent = '„2"-Präfix — Otto lehnt ggf. ab'; hint.className = 'otto-vd-hint otto-vd-hint--warn'; inp.classList.add('is-warn'); inp.classList.remove('is-invalid'); }
      else { hint.textContent = '✓'; hint.className = 'otto-vd-hint otto-vd-hint--ok'; inp.classList.remove('is-invalid', 'is-warn'); }
    };
    inp.addEventListener('input', paint); paint();
  });

  // Schnell-Pflege (EAN/Preis): geänderte Kanal-Zeilen sammeln → otto-variant-rows.save.
  // Liest den DOM-State erst zur Ausführungszeit; wird über die Save-Registry des Detail-Panes
  // vom globalen „Speichern"-Button ausgeführt (kein eigener Button mehr).
  interface StockChange {
    childOttoListingId: string; sku: string | null;
    min: number | null; max: number | null; minMaxChanged: boolean;
    storageIds: string[] | null; whChanged: boolean;
  }

  const save = async (): Promise<void> => {
    const payload: Array<{ articleId: string; ean?: string; priceEur?: number }> = [];
    const stockChanges: StockChange[] = [];
    let invalid = false;
    host.querySelectorAll<HTMLTableRowElement>('tr[data-vd-row]').forEach((tr) => {
      const articleId = tr.dataset['vdRow']!;
      const orig = rows.find((r) => r.articleId === articleId);
      const eanInp = tr.querySelector<HTMLInputElement>('input[data-vd-ean]');
      const priceInp = tr.querySelector<HTMLInputElement>('input[data-vd-price]');
      if (eanInp || priceInp) {
        const ean = eanInp?.value.trim() ?? '';
        const priceStr = priceInp?.value.trim() ?? '';
        const entry: { articleId: string; ean?: string; priceEur?: number } = { articleId };
        let changed = false;
        if (ean && ean !== (orig?.ean ?? '')) { if (!validateEan(ean).ok) invalid = true; entry.ean = ean; changed = true; }
        if (priceStr) { const p = Number(priceStr.replace(',', '.')); if (Number.isFinite(p) && p !== (orig?.priceEur ?? null)) { entry.priceEur = p; changed = true; } }
        if (changed) payload.push(entry);
      }
      // Bestand/Min/Max/Lager — nur Kanal-Kinder (childOttoListingId gesetzt).
      const minInp = tr.querySelector<HTMLInputElement>('input[data-vd-min]');
      const maxInp = tr.querySelector<HTMLInputElement>('input[data-vd-max]');
      const whChecks = tr.querySelectorAll<HTMLInputElement>('input[data-vd-wh]');
      if (orig?.childOttoListingId && (minInp || maxInp || whChecks.length > 0)) {
        const min = parseIntOrNull(minInp?.value);
        const max = parseIntOrNull(maxInp?.value);
        const minMaxChanged = min !== (orig.ownMin ?? null) || max !== (orig.ownMax ?? null);
        const checkedIds = Array.from(whChecks).filter((c) => c.checked).map((c) => c.value);
        const whChanged = !sameIdSet(checkedIds, orig.ownStorageIds ?? []);
        const storageIds = checkedIds.length > 0 ? checkedIds : null;
        if (minMaxChanged || whChanged) {
          stockChanges.push({ childOttoListingId: orig.childOttoListingId, sku: orig.sku, min, max, minMaxChanged, storageIds, whChanged });
        }
      }
    });
    if (invalid) throw new Error('Mindestens eine Varianten-EAN ist ungültig.');

    if (payload.length > 0) {
      const res = await callFn<{ results: Array<{ articleId: string; ean?: { ok: boolean; message?: string }; price?: { ok: boolean; message?: string } }> }>(
        ctx, 'otto-variant-rows', { action: 'save', listingId: args.listingId, rows: payload },
      );
      const results = res.results ?? [];
      const failCount = results.filter((r) => (r.ean && !r.ean.ok) || (r.price && !r.price.ok)).length;
      if (failCount > 0) throw new Error(`${failCount} Variante(n) mit Fehler beim EAN/Preis-Speichern.`);
    }

    // Min/Max/Lager je Kind laufen NICHT über otto-variant-rows, sondern direkt über die
    // bestehenden RPCs (wie im Einzel/Vater-Wertblock, otto-list-stock.ts).
    for (const c of stockChanges) {
      if (c.minMaxChanged) {
        // Listing-zentriert (wie otto-list-stock.ts): löst die Mapping-Zeile per aid/SKU auf und
        // legt sie ggf. an — unabhängig davon, ob das Kind eine vario_article_number trägt.
        const mm = await ctx.supabase.rpc('otto_set_listing_min_max_by_id', { _listing_id: c.childOttoListingId, _min: c.min, _max: c.max });
        if (mm.error) throw new Error(mm.error.message);
      }
      if (c.whChanged) {
        const wh = await ctx.supabase.rpc('otto_set_listing_warehouse', { _listing_id: c.childOttoListingId, _storage_ids: c.storageIds });
        if (wh.error) throw new Error(wh.error.message);
      }
    }

    // Nach Min/Max/Lager-Änderung den Vario-Bestand (Vater → alle Kanal-Kinder) auffrischen, damit
    // das Grid den aktuellen Stand zeigt. Die Änderung wird beim nächsten 10-Min-Push-Cron (bzw.
    // sofort über „Aktualisieren") an Otto übertragen — kein event-getriebener Direkt-Push mehr.
    if (stockChanges.length > 0) {
      try {
        await ctx.supabase.rpc('otto_enqueue_stock_refresh', { _listing_id: args.listingId });
      } catch { /* Refresh best-effort */ }
    }

    if (payload.length === 0 && stockChanges.length === 0) return; // Nichts geändert → No-Op.
    await renderVariantDataGrid(host, ctx, args);
  };
  ctx.registerSaver?.('variant-data', save);

  // "Verkaufskanal anlegen" — legt Vario-Kanal-Listung + Kind-Lister-Zeile an, dann Reload.
  host.querySelectorAll<HTMLButtonElement>('button[data-vd-create]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const childArticleId = btn.dataset['vdCreate']!;
      btn.disabled = true;
      btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lege an…';
      try {
        await callFn(ctx, 'otto-variant-channel-create', { parentListingId: args.listingId, childArticleId });
        showToast('Otto-Verkaufskanal für die Variante angelegt.', 'success');
        await renderVariantDataGrid(host, ctx, args);
      } catch (e) {
        showToast(`Kanal-Anlage fehlgeschlagen: ${(e as Error).message}`, 'error');
        btn.disabled = false;
        btn.innerHTML = '<span class="v-icon fa-regular fa-plus"></span> Verkaufskanal anlegen';
      }
    });
  });
}

function rowHtml(r: Row, axisKeys: string[], varioBaseUrl: string, warehouses: WarehouseRow[]): string {
  const skuText = escapeHtml(r.sku ?? '—');
  const channelHref = varioBaseUrl && r.channelListingId
    ? `${varioBaseUrl}/products/articles/${encodeURIComponent(r.articleId)}/listings/${encodeURIComponent(r.channelListingId)}`
    : null;
  const articleHref = varioBaseUrl ? `${varioBaseUrl}/products/articles/${encodeURIComponent(r.articleId)}` : null;
  const skuHref = channelHref ?? articleHref;
  const linkIcon = '<span class="v-icon fa-regular fa-arrow-up-right-from-square"></span>';
  const skuCell = skuHref
    ? `<a class="kl-vsku" href="${escapeAttr(skuHref)}" target="_blank" rel="noopener" title="In Vario öffnen">${skuText}${linkIcon}</a>`
    : `<span class="kl-vsku">${skuText}</span>`;
  // Otto-Kanal-Spalte: angelegt → Status „im Kanal"; sonst der „Verkaufskanal anlegen"-Button.
  // Die frühere separate „Aktion"-Spalte wurde entfernt — der Anlegen-Button lebt jetzt hier,
  // das redundante „angelegt" entfällt (den Status zeigt „im Kanal" bereits).
  const channelBadge = r.childOttoListingId
    ? '<span class="kl-chan-on" title="Variante ist im Otto-Verkaufskanal angelegt"><span class="kl-chan-d"></span>im Kanal</span>'
    : `<button type="button" class="kl-vopen" data-vd-create="${escapeAttr(r.articleId)}" title="Noch kein Otto-Verkaufskanal — Variante wird sonst nicht übertragen"><span class="v-icon fa-regular fa-plus"></span> Verkaufskanal anlegen</button>`;
  // Pflichtfeld-Fortschritt nur sinnvoll, sobald im Kanal; sonst Platzhalter.
  const progress = r.childOttoListingId ? progressBar(r.completeness, r) : '<span class="otto-vd-na">—</span>';
  // EAN/Preis: nur Kanal-Varianten editierbar (Schnell-Pflege); sonst Platzhalter.
  const srcTitle = (s: string | null | undefined): string =>
    s === 'mapping' ? 'Vorschau aus Mapping (Vaterartikel)'
    : s === 'listing' || s === 'channel' ? 'im Otto-Kanal gepflegt'
    : s === 'article' ? 'aus Artikelstammdaten'
    : '';
  const eanCell = r.childOttoListingId
    ? `<input class="kl-vinp" data-vd-ean type="text" inputmode="numeric" autocomplete="off" value="${escapeAttr(r.ean ?? '')}" placeholder="EAN" title="${escapeAttr(srcTitle(r.eanSource))}" />
       <div data-vd-ean-hint class="otto-vd-hint"></div>`
    : '<span class="otto-vd-na">—</span>';
  // Wert = ECHTER Otto-Kanalpreis (priceEur). Fehlt er, bleibt das Feld leer und zeigt die
  // Mapping-Vorschau nur als grauen Platzhalter (kein vorbefüllter Wert → Change-Detection greift,
  // sobald der Händler etwas eingibt; die Vorschau wird nie ungewollt in den Kanal geschrieben).
  const pricePlaceholder = r.priceEur == null && r.pricePreviewEur != null
    ? `Vorschau: ${r.pricePreviewEur}`
    : '0,00';
  const priceTitle = r.priceSource === 'channel'
    ? 'im Otto-Kanal gepflegt'
    : (r.priceEur == null && r.pricePreviewEur != null ? 'Vorschau aus Stammdaten — noch kein Kanalpreis gesetzt' : '');
  const priceCell = r.childOttoListingId
    ? `<input class="kl-vinp kl-vinp--price" data-vd-price type="text" inputmode="decimal" autocomplete="off" value="${r.priceEur != null ? String(r.priceEur) : ''}" placeholder="${escapeAttr(pricePlaceholder)}" title="${escapeAttr(priceTitle)}" />`
    : '<span class="otto-vd-na">—</span>';
  // Bestand (read-only) + Min/Max/Lager: nur Kanal-Kinder editierbar (Override je Kind, vererbt vom Vater-Default).
  const stockCell = r.childOttoListingId
    ? `<span class="otto-vd-stock">${r.varioStock ?? '—'}</span>`
    : '<span class="otto-vd-na">—</span>';
  const minCell = r.childOttoListingId
    ? `<input class="kl-vinp" data-vd-min type="number" inputmode="numeric" min="0" step="1" autocomplete="off" value="${r.ownMin ?? ''}" placeholder="${r.effMin != null && r.ownMin == null ? `geerbt: ${r.effMin}` : ''}" />`
    : '<span class="otto-vd-na">—</span>';
  const maxCell = r.childOttoListingId
    ? `<input class="kl-vinp" data-vd-max type="number" inputmode="numeric" min="0" step="1" autocomplete="off" value="${r.ownMax ?? ''}" placeholder="${r.effMax != null && r.ownMax == null ? `geerbt: ${r.effMax}` : ''}" />`
    : '<span class="otto-vd-na">—</span>';
  const whCell = r.childOttoListingId
    ? warehouseCheckboxes(r, warehouses)
    : '<span class="otto-vd-na">—</span>';
  return `
    <tr data-vd-row="${escapeAttr(r.articleId)}">
      <td>${skuCell}</td>
      ${axisKeys.map((a) => `<td>${escapeHtml(r.attrs[a] ?? '—')}</td>`).join('')}
      <td>${channelBadge}</td>
      <td>${progress}</td>
      <td class="otto-vd-eancell">${eanCell}</td>
      <td>${priceCell}</td>
      <td>${stockCell}</td>
      <td>${minCell}</td>
      <td>${maxCell}</td>
      <td>${whCell}</td>
    </tr>`;
}

/** Lager-Mehrfachauswahl als kompakte Checkbox-Gruppe (statt Einzel-`<select>`, seit Migration 0252
 *  sind Lager Arrays). Angehakt = Lager-id in `r.ownStorageIds`. Ohne eigene Auswahl wird ein Hinweis
 *  auf die geerbten Lager (`r.effStorageIds`, Vater/Storefront-Default) angezeigt. */
function warehouseCheckboxes(r: Row, warehouses: WarehouseRow[]): string {
  // Vorauswahl: eigene Auswahl des Kindes, sonst die geerbten (Vater/Einstellungen) Lager —
  // so sind die in den Einstellungen definierten Lager immer vorausgewählt.
  const ownArr = r.ownStorageIds ?? [];
  const effIds = r.effStorageIds ?? [];
  const pre = new Set(ownArr.length > 0 ? ownArr : effIds);
  const items = warehouses.map((w) => {
    const label = w.displayName || w.number || w.id;
    const checked = pre.has(w.id) ? ' checked' : '';
    return `<label class="otto-vd-wh-item" title="${escapeAttr(label)}${w.salesStorage ? ' · Verkauf' : ''}">
      <input type="checkbox" data-vd-wh value="${escapeAttr(w.id)}"${checked} /> <span>${escapeHtml(label)}</span>
    </label>`;
  }).join('');
  const group = items || '<span class="otto-vd-na">keine Lager</span>';
  const note = ownArr.length === 0 && effIds.length > 0
    ? `<div class="otto-vd-wh-inherit">aus Einstellungen vorausgewählt: ${escapeHtml(warehouseLabels(effIds, warehouses))}</div>`
    : '';
  return `<div class="otto-vd-wh-group">${group}</div>${note}`;
}
/** Lager-ids → menschenlesbare Labels (fällt auf die id zurück, falls unbekannt), komma-getrennt. */
function warehouseLabels(ids: string[], warehouses: WarehouseRow[]): string {
  return ids.map((id) => {
    const w = warehouses.find((x) => x.id === id);
    return w ? (w.displayName || w.number || w.id) : id;
  }).join(', ');
}
/** Mengenvergleich zweier Lager-id-Listen (Reihenfolge egal). */
function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = new Set(a);
  return b.every((id) => sa.has(id));
}

/** Ganzzahl aus Input-Wert oder null (leer/ungültig). */
function parseIntOrNull(v: string | undefined | null): number | null {
  if (v == null) return null;
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

/** GS1-Validierung (Mod-10) für die Schnell-Pflege. */
function validateEan(ean: string): { ok: boolean; leadingTwo: boolean; reason?: string } {
  if (!/^\d+$/.test(ean)) return { ok: false, leadingTwo: false, reason: 'nur Ziffern' };
  if (![8, 12, 13, 14].includes(ean.length)) return { ok: false, leadingTwo: ean.startsWith('2'), reason: `${ean.length} Stellen (8/13)` };
  const d = ean.split('').map(Number);
  const check = d.pop()!;
  let sum = 0;
  for (let i = d.length - 1, w = 3; i >= 0; i -= 1, w = w === 3 ? 1 : 3) sum += d[i]! * w;
  if ((10 - (sum % 10)) % 10 !== check) return { ok: false, leadingTwo: ean.startsWith('2'), reason: 'Prüfziffer' };
  return { ok: true, leadingTwo: ean.startsWith('2') };
}

/** Fortschrittsbalken: erfüllte Pflichtfelder (EAN, Preis, Bild) je Variante. */
function progressBar(pct: number, r: Row): string {
  const miss = [!r.eanOk ? 'EAN' : null, !r.priceOk ? 'Preis' : null, !r.hasImage ? 'Bild' : null].filter(Boolean);
  const title = miss.length ? `fehlt: ${miss.join(', ')}` : 'alle Pflichtfelder erfüllt';
  const check = pct >= 100
    ? '<span class="kl-pf-ck"><span class="v-icon fa-solid fa-check"></span></span>'
    : '';
  return `<span class="kl-pf-cell" title="${escapeAttr(title)}">
    <span class="kl-pf-bar"><i style="width:${pct}%"></i></span>
    <span class="kl-pf-pct">${pct}%</span>${check}
  </span>`;
}

async function callFn<T>(ctx: VariantDataCtx, name: string, body: unknown): Promise<T> {
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    let msg = `${name} ${res.status}`;
    try { const j = JSON.parse(t); if (j?.error?.message) msg = j.error.message; } catch { /* keep */ }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

function banner(kind: 'warn' | 'error' | 'info', msg: string): string {
  const cls = kind === 'error' ? 'v-banner-danger' : kind === 'warn' ? 'v-banner-warn' : 'v-banner-info';
  const icon = kind === 'error' ? 'fa-circle-xmark' : kind === 'warn' ? 'fa-triangle-exclamation' : 'fa-circle-info';
  return `<div class="v-pa-md"><div class="v-banner ${cls}"><span class="v-icon fa-light ${icon}"></span><div>${msg}</div></div></div>`;
}
function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}
function escapeAttr(s: string): string { return escapeHtml(s); }
